import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import placeholderImg from '../../assets/img.jpg';
import './Categories.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
    // Initialize from URL params if present
    const params = new URLSearchParams(window.location.search);
    const initCategory = params.get('category');
    const initSearch = params.get('search') || '';
    if (initSearch) setSearchQuery(initSearch);
    if (initCategory) setSelectedCategory(initCategory);
    fetchProducts({ category: initCategory, search: initSearch });
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/ecommerce/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async ({ category = null, search = '' } = {}) => {
    try {
      setLoading(true);
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      const response = await api.get('/api/ecommerce/products', { params });
      if (response.data.success) {
        const payload = response.data.data;
        // Backend returns { products, pagination }
        setProducts(Array.isArray(payload?.products) ? payload.products : []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    const params = new URLSearchParams(window.location.search);
    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    if (searchQuery) params.set('search', searchQuery); else params.delete('search');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
    fetchProducts({ category, search: searchQuery });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    const params = new URLSearchParams(window.location.search);
    if (q) params.set('search', q); else params.delete('search');
    if (selectedCategory) params.set('category', selectedCategory);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
    fetchProducts({ category: selectedCategory, search: q });
  };

  // Use server-provided list; keep a light client-side filter fallback
  const filteredProducts = Array.isArray(products)
    ? products.filter((product) => {
        const inv = product?.inventory || {};
        const name = inv.itemName || '';
        const desc = inv.productDescription || '';
        const q = searchQuery.toLowerCase();
        return !q || name.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
      })
    : [];

  if (loading && (!Array.isArray(products) || products.length === 0)) {
    return (
      <div className="categories-container">
        <div className="categories-loading">
          <h2>Loading categories...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="categories-container">
        <div className="categories-error">
          <h2>Error loading categories</h2>
          <p>{error}</p>
          <button onClick={() => fetchProducts()} className="btn btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-container">
      <div className="categories-header">
        <h1>Browse by Category</h1>
        <p>Discover products organized by category</p>
        
        <form onSubmit={handleSearch} className="categories-search">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">
            Search
          </button>
        </form>
      </div>

      <div className="categories-content">
        <div className="categories-sidebar">
          <h3>Categories</h3>
          <div className="category-list">
            <button
              className={`category-item ${!selectedCategory ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(null);
                fetchProducts();
              }}
            >
              All Products
            </button>
            {categories.map((category, index) => (
              <button
                key={index}
                className={`category-item ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="categories-main">
          {selectedCategory && (
            <div className="category-header">
              <h2>{selectedCategory}</h2>
              <p>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found</p>
            </div>
          )}

          {loading ? (
            <div className="products-loading">
              <h3>Loading products...</h3>
            </div>
          ) : (!Array.isArray(filteredProducts) || filteredProducts.length === 0) ? (
            <div className="products-empty">
              <h3>No products found</h3>
              <p>Try selecting a different category or search term.</p>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map((product) => {
                const inv = product.inventory || {};
                const currentPrice = inv.discountedPrice || inv.sellingPrice;
                const hasDiscount = inv.discountedPrice && inv.sellingPrice && inv.sellingPrice > inv.discountedPrice;
                const discountPct = hasDiscount ? Math.round(100 - (inv.discountedPrice / inv.sellingPrice) * 100) : null;
                const imageSrc = Array.isArray(inv.images) && inv.images.length > 0 ? inv.images[0] : placeholderImg;
                return (
                <div key={product._id} className="product-card">
                  <div className="product-image">
                    <Link to={`/ecommerce/products/${product.slug}`}>
                      <img 
                        src={imageSrc} 
                        alt={inv.itemName || 'Product'}
                      />
                    </Link>
                    {hasDiscount && (
                      <div className="product-discount">
                        -{discountPct}%
                      </div>
                    )}
                  </div>
                  
                  <div className="product-details">
                    <h3>
                      <Link to={`/ecommerce/products/${product.slug}`}>
                        {inv.itemName || 'Unnamed Product'}
                      </Link>
                    </h3>
                    <p className="product-description">
                      {(inv.productDescription || '').substring(0, 100)}...
                    </p>
                    {inv.itemCode && <p className="product-code">SKU: {inv.itemCode}</p>}
                    
                    {/* Variants not available in current API shape */}
                    
                    <div className="product-price">
                      {currentPrice !== undefined && (
                        <span className="current-price">${currentPrice}</span>
                      )}
                      {hasDiscount && (
                        <span className="original-price">${inv.sellingPrice}</span>
                      )}
                    </div>
                    
                    <div className="product-actions">
                      <Link 
                        to={`/ecommerce/products/${product.slug}`}
                        className="btn btn-primary"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Categories;
